import { Button } from '../../../ui/components/ui/button';
import { Input } from '../../../ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/components/ui/select';
import { MAIL_SENDER_LABELS, TEMPLATE_LABELS } from '../constants/mapping';
import { MailSenderResponseDto, MassMailMetaResponseDto, OrganizationRecipientResponseDto, SendMailRequestResponseDto } from '../types/response';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { convertHtmlToEditorState } from '../../../lib/hooks/utils';
import { CreateEditorWithState } from '../../order/create/create-editor';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../ui/components/ui/form';
import { useForm } from 'react-hook-form';
import { EditorState } from 'react-draft-wysiwyg';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../ui/components/ui/dialog';
import { Label } from '../../../ui/components/ui/label';
import useApiRequest from '../../../lib/hooks/useRequest';
import { toast } from 'sonner';
import { TestRequestDto, SendMailRequestDto } from '../types/request';

interface SendMailFormProps {
  mailMeta: MassMailMetaResponseDto;
  selectedSender: MailSenderResponseDto;
  setSelectedSender: (sender: MailSenderResponseDto) => void;
  selectedTemplate: string;
  setSelectedTemplate: (template: string) => void;
  messageContent: string;
  setMessageContent: (content: string) => void;
  subject: string;
  setSubject: (subject: string) => void;
  selectedRecipients: OrganizationRecipientResponseDto[];
  onSend: (requestId: string) => void;
}

const stripHtmlContent = (value: string) =>
  value
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const SendMailForm = ({
  mailMeta,
  selectedSender,
  setSelectedSender,
  selectedTemplate,
  setSelectedTemplate,
  messageContent,
  setMessageContent,
  subject,
  setSubject,
  selectedRecipients,
  onSend
}: SendMailFormProps) => {
  const [editorState, setEditorState] = useState<EditorState>(() => convertHtmlToEditorState(messageContent || ''));
  const { apiRequest } = useApiRequest();

  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [isSendingTestMail, setIsSendingTestMail] = useState(false);
  const [testRecipient, setTestRecipient] = useState<string>('');
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const sendMailSchema = z.object({
    mailSender: z
      .string()
      .refine((value) => mailMeta.mailSenders.some((sender) => sender.id === value), { message: 'Bitte einen Absender auswählen.' }),
    mailTemplate: z.string().refine((value) => mailMeta.mailTemplates.includes(value), { message: 'Bitte ein Template auswählen.' }),
    subject: z.string().trim().min(1, { message: 'Betreff darf nicht leer sein.' }),
    content: z.string().refine(
      (value) => {
        return stripHtmlContent(value).length > 0;
      },
      { message: 'Nachricht darf nicht leer sein.' }
    )
  });

  type SendMailSchema = z.infer<typeof sendMailSchema>;

  const form = useForm<SendMailSchema>({
    resolver: zodResolver(sendMailSchema),
    defaultValues: {
      mailSender: selectedSender.id,
      mailTemplate: selectedTemplate,
      subject: subject,
      content: messageContent
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedRecipient = window.localStorage.getItem('massMailTestRecipient');
    if (storedRecipient) {
      setTestRecipient(storedRecipient);
    }
  }, []);

  const handleSendTestMail = useCallback(async () => {
    if (!testRecipient.trim()) {
      toast.error('Bitte Empfänger angeben.');
      return;
    }

    setIsSendingTestMail(true);

    try {
      const response = await apiRequest<void>('mass-mails/internal/test', 'POST', {
        body: {
          template: selectedTemplate,
          content: messageContent,
          recipient: testRecipient.trim(),
          mailSender: selectedSender.id,
          subject
        } as TestRequestDto
      });

      if (response.status >= 200 && response.status < 300 && !response.error) {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('massMailTestRecipient', testRecipient.trim());
        }
        toast.success('Test-E-Mail wurde gesendet.');
        setIsTestDialogOpen(false);
      } else {
        toast.error(typeof response.error === 'string' ? response.error : 'Test-E-Mail konnte nicht gesendet werden.');
      }
    } catch (error) {
      toast.error('Test-E-Mail konnte nicht gesendet werden.');
      console.error('Failed to send test mail', error);
    } finally {
      setIsSendingTestMail(false);
    }
  }, [apiRequest, messageContent, selectedSender.id, selectedTemplate, subject, testRecipient]);

  const handleContentChange = (newState: EditorState) => {
    setEditorState(newState);
    let html = draftToHtml(convertToRaw(newState.getCurrentContent()));

    html = html
      .replace(/<ul>/g, '<ul style="display: inline-block; text-align: left;">')
      .replace(/<ol>/g, '<ol style="display: inline-block; text-align: left;">');

    if (stripHtmlContent(html).trim().length <= 0) {
      setMessageContent('');
      form.setValue('content', '');
      form.trigger('content');
      return;
    }

    setMessageContent(html);
    form.setValue('content', html);
    form.trigger('content');
  };

  const handleSenderChange = (value: string) => {
    const sender = mailMeta.mailSenders.find((sender) => sender.id === value);
    if (!sender) return;

    form.setValue('mailSender', sender.id);
    setSelectedSender(sender);
  };

  const handleFormSubmit = useCallback(() => {
    if (selectedRecipients.length === 0) {
      toast.error('Bitte wähle mindestens einen Empfänger aus.');
      return;
    }
    setIsConfirmDialogOpen(true);
  }, [selectedRecipients]);

  const onSubmit = useCallback(
    async (data: SendMailSchema) => {
      if (selectedRecipients.length === 0) {
        toast.error('Bitte wähle mindestens einen Empfänger aus.');
        return;
      }

      setIsSendingMail(true);
      setIsConfirmDialogOpen(false);

      try {
        const sendRequest: SendMailRequestDto = {
          mailSender: data.mailSender,
          template: data.mailTemplate,
          subject: data.subject,
          content: data.content,
          organizationIds: selectedRecipients.map((recipient) => recipient._id)
        };

        const response = await apiRequest<SendMailRequestResponseDto>('mass-mails/internal/send', 'POST', {
          body: sendRequest
        });

        if (response.data && response.data._id) {
          onSend(response.data._id);
          setIsSendingMail(false);
        } else {
          toast.error('Fehler beim Erstellen der Versandanfrage.');
          setIsSendingMail(false);
        }
      } catch (error) {
        toast.error('Fehler beim Senden der E-Mail.');
        console.error('Failed to send mail', error);
        setIsSendingMail(false);
      }
    },
    [apiRequest, selectedRecipients, onSend]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="flex flex-col gap-5 h-full">
        <FormField
          control={form.control}
          name="mailSender"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Absender</FormLabel>
              <Select value={field.value} onValueChange={handleSenderChange}>
                <FormControl>
                  <SelectTrigger id="sender">
                    <SelectValue placeholder="Absender auswählen..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {mailMeta.mailSenders.map((sender) => (
                    <SelectItem key={sender.id} value={sender.id}>
                      {MAIL_SENDER_LABELS[sender.id] || `${sender.fromName} <${sender.fromEmail}>`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="mailTemplate"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>E-Mail Template</FormLabel>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  setSelectedTemplate(value);
                  field.onChange(value);
                }}>
                <FormControl>
                  <SelectTrigger id="template">
                    <SelectValue placeholder="Template auswählen..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {mailMeta.mailTemplates.map((template) => (
                    <SelectItem key={template} value={template}>
                      {TEMPLATE_LABELS[template] || template}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Betreff</FormLabel>
              <FormControl>
                <Input
                  value={field.value}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    field.onChange(e.target.value);
                  }}
                  placeholder="Betreff"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Nachricht</FormLabel>
              <FormControl>
                <div ref={field.ref}>
                  <CreateEditorWithState editorState={editorState} setEditorState={handleContentChange} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between gap-2">
          <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" className="w-full flex-1" disabled={!form.formState.isValid}>
                Test E-Mail
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Test-E-Mail senden</DialogTitle>
                <DialogDescription>Gib eine Empfängeradresse ein, um eine Test-E-Mail zu senden.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="test-recipient">Empfänger</Label>
                <Input
                  id="test-recipient"
                  type="email"
                  placeholder="empfaenger@example.com"
                  value={testRecipient}
                  onChange={(event) => setTestRecipient(event.target.value)}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsTestDialogOpen(false)} disabled={isSendingTestMail}>
                  Abbrechen
                </Button>
                <Button type="button" onClick={handleSendTestMail} disabled={isSendingTestMail}>
                  {isSendingTestMail ? 'Test-E-Mail senden...' : 'Test-E-Mail senden'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button className="w-full flex-1" type="submit" disabled={!form.formState.isValid || isSendingMail || selectedRecipients.length === 0}>
            {isSendingMail ? 'Wird vorbereitet...' : 'E-Mail senden'}
          </Button>
        </div>
      </form>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>E-Mail Versand bestätigen</DialogTitle>
            <DialogDescription>
              Möchten Sie wirklich {selectedRecipients.length} E-Mail{selectedRecipients.length !== 1 ? 's' : ''} versenden?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsConfirmDialogOpen(false)} disabled={isSendingMail}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={() => {
                const formData = form.getValues();
                onSubmit(formData);
              }}
              disabled={isSendingMail}>
              {isSendingMail ? 'Wird gesendet...' : 'Senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
